// =============================================================================
// Games data layer — localStorage-first (like Music started), no DB/server yet.
//
// Holds: the game catalog, per-game high scores + level/progress, a roster of
// people you can compete with (family / friends / clients / peers — these are
// NOT akountant members), a per-game social leaderboard, lifetime stats, and a
// shareable "challenge link" encoder/decoder that powers the viral, attract-a-
// non-member loop (someone opens your challenge URL, tries to beat your score,
// and gets nudged to sign up).
//
// Everything fires `akt_games_changed` on write so open views re-render and
// other tabs stay in sync.
// =============================================================================

import {
  syncMyScore, syncFriend, syncFriendInvited, removeFriendRemote, syncPeerScore,
  hasGamesApi, fetchScores, fetchFriends, pushScore, pushFriend, pushPeerScore,
} from './gamesApi';

export type GameId =
  | '2048' | 'snake' | 'memory' | 'tictactoe' | 'minesweeper'
  | 'tetris' | 'breakout' | 'sudoku' | 'connect4'
  | 'pong' | 'simon' | 'solitaire';

export interface GameMeta {
  id: GameId;
  title: string;
  tagline: string;
  emoji: string;
  /** Accent color used on the arcade card + leaderboard chips. */
  color: string;
  /** Human label for the score (e.g. "points", "best tile"). */
  scoreLabel: string;
  /** Short "how to play" line. */
  how: string;
  /** Levels this game ships with (difficulty / progression). */
  levels: number;
}

export const GAMES: GameMeta[] = [
  {
    id: '2048',
    title: '2048',
    tagline: 'Slide, merge, chase the big tile.',
    emoji: '🔢',
    color: '#edc22e',
    scoreLabel: 'points',
    how: 'Use arrow keys (or swipe) to slide tiles. Equal tiles merge. Reach the target tile to clear a level.',
    levels: 5,
  },
  {
    id: 'snake',
    title: 'Snake',
    tagline: 'Eat, grow, don’t bite yourself.',
    emoji: '🐍',
    color: '#4caf50',
    scoreLabel: 'points',
    how: 'Arrow keys / WASD to steer. Eat food to grow. Speed ramps up each level. Don’t hit the walls or your tail.',
    levels: 6,
  },
  {
    id: 'memory',
    title: 'Memory Match',
    tagline: 'Flip the pairs from memory.',
    emoji: '🧠',
    color: '#7e57c2',
    scoreLabel: 'points',
    how: 'Flip two cards. Matching pairs stay up. Clear the board in as few moves and as fast as you can — bigger grids each level.',
    levels: 5,
  },
  {
    id: 'tictactoe',
    title: 'Tic-Tac-Toe',
    tagline: 'Outsmart the machine.',
    emoji: '⭕',
    color: '#29b6f6',
    scoreLabel: 'win streak',
    how: 'Get three in a row before the computer does. Harder levels think further ahead. Build a win streak for points.',
    levels: 3,
  },
  {
    id: 'minesweeper',
    title: 'Minesweeper',
    tagline: 'Logic under pressure.',
    emoji: '💣',
    color: '#ef5350',
    scoreLabel: 'points',
    how: 'Reveal safe squares, flag the mines. Numbers show nearby mines. Clear the field — bigger fields, more mines each level.',
    levels: 4,
  },
  {
    id: 'tetris',
    title: 'Tetris',
    tagline: 'Stack, line up, clear.',
    emoji: '🧱',
    color: '#5c6bc0',
    scoreLabel: 'points',
    how: 'Move and rotate falling blocks to complete full rows. Clear 10 lines to beat a level. ← → move, ↑ rotate, ↓ soft-drop, space hard-drop.',
    levels: 8,
  },
  {
    id: 'breakout',
    title: 'Breakout',
    tagline: 'Bounce, smash every brick.',
    emoji: '🏓',
    color: '#26c6da',
    scoreLabel: 'points',
    how: 'Steer the paddle to keep the ball alive and clear all the bricks. Three lives. Faster ball + more bricks each level.',
    levels: 5,
  },
  {
    id: 'sudoku',
    title: 'Sudoku',
    tagline: 'Pure logic, 9×9.',
    emoji: '🧩',
    color: '#26a69a',
    scoreLabel: 'points',
    how: 'Fill the grid so every row, column and 3×3 box has 1–9. Tap a cell then a number. Faster solves on harder levels score more.',
    levels: 4,
  },
  {
    id: 'connect4',
    title: 'Connect Four',
    tagline: 'Four in a row beats the AI.',
    emoji: '🔴',
    color: '#ec407a',
    scoreLabel: 'win streak',
    how: 'Drop discs into columns. Connect four of yours — horizontally, vertically or diagonally — before the computer does. Build a win streak.',
    levels: 3,
  },
  {
    id: 'pong',
    title: 'Pong',
    tagline: 'The original. You vs the CPU.',
    emoji: '🏓',
    color: '#66bb6a',
    scoreLabel: 'win streak',
    how: 'Move your paddle with mouse, finger or ↑↓. First to 7 points wins the match. Faster CPU each level. Win to build a streak.',
    levels: 3,
  },
  {
    id: 'simon',
    title: 'Simon',
    tagline: 'Watch, remember, repeat.',
    emoji: '🎵',
    color: '#ab47bc',
    scoreLabel: 'rounds',
    how: 'Watch the pads light up in a growing sequence, then repeat it back. One more step each round — how far can your memory go?',
    levels: 3,
  },
  {
    id: 'solitaire',
    title: 'Solitaire',
    tagline: 'Klondike — the desktop classic.',
    emoji: '🃏',
    color: '#2e7d32',
    scoreLabel: 'points',
    how: 'Build the four foundations Ace→King by suit. Stack the tableau down in alternating colors. Click a card, then its destination. Click the stock to draw.',
    levels: 2,
  },
];

export function getGame(id: GameId): GameMeta | undefined {
  return GAMES.find(g => g.id === id);
}

// ----------------------------------------------------------------------------
// Storage keys
// ----------------------------------------------------------------------------
const K_SCORES = 'akt_game_scores';      // { [gameId]: { best, plays, lastScore, updatedAt, history[] } }
const K_PROGRESS = 'akt_game_progress';  // { [gameId]: { level, unlockedLevel } }
const K_FRIENDS = 'akt_game_friends';    // Friend[]
const K_BOARD = 'akt_game_leaderboard';  // { [gameId]: { [friendId]: { best, name, updatedAt } } }

function emitChanged() {
  try {
    window.dispatchEvent(new Event('akt_games_changed'));
  } catch { /* SSR / non-browser */ }
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota / private mode */ }
  emitChanged();
}

// ----------------------------------------------------------------------------
// Who's playing (from the session — used to label your own scores)
// ----------------------------------------------------------------------------
export interface PlayerIdentity {
  id: number | null;
  name: string;
  isMember: boolean;
}

export function getPlayer(): PlayerIdentity {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (raw) {
      const u = JSON.parse(raw);
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || 'You';
      return { id: u.id != null ? Number(u.id) : null, name, isMember: true };
    }
  } catch { /* fall through */ }
  // Guest (e.g. a non-member who opened a challenge link on the public page)
  const guest = read<{ name: string }>('akt_games_guest', { name: '' });
  return { id: null, name: guest.name || 'Guest', isMember: false };
}

export function setGuestName(name: string) {
  write('akt_games_guest', { name: name.trim() });
}

// ----------------------------------------------------------------------------
// Scores
// ----------------------------------------------------------------------------
export interface ScoreRecord {
  best: number;
  plays: number;
  lastScore: number;
  updatedAt: string;
  /** Recent results, newest first (capped). */
  history: { score: number; level: number; at: string }[];
}

type ScoreMap = Partial<Record<GameId, ScoreRecord>>;

export function getScore(id: GameId): ScoreRecord {
  const all = read<ScoreMap>(K_SCORES, {});
  return all[id] || { best: 0, plays: 0, lastScore: 0, updatedAt: '', history: [] };
}

export function getHighScore(id: GameId): number {
  return getScore(id).best;
}

/** Record a finished game. Returns whether it set a new personal best. */
export function recordScore(id: GameId, score: number, level = 1): boolean {
  const all = read<ScoreMap>(K_SCORES, {});
  const prev = all[id] || { best: 0, plays: 0, lastScore: 0, updatedAt: '', history: [] };
  const isBest = score > prev.best;
  const at = new Date().toISOString();
  all[id] = {
    best: Math.max(prev.best, score),
    plays: prev.plays + 1,
    lastScore: score,
    updatedAt: at,
    history: [{ score, level, at }, ...prev.history].slice(0, 20),
  };
  write(K_SCORES, all);

  // Your own row on the social leaderboard always reflects your best.
  const me = getPlayer();
  if (score > 0) logScore(id, `self:${me.id ?? 'me'}`, me.name, all[id]!.best);

  // Best-effort server sync (members only; no-ops offline / for guests).
  if (me.isMember && score > 0) syncMyScore(id, all[id]!.best, all[id]!.plays, level);

  // Daily challenge + achievements bookkeeping.
  markDailyDone(id);
  checkAchievements();
  return isBest;
}

export function totalPlays(): number {
  const all = read<ScoreMap>(K_SCORES, {});
  return GAMES.reduce((sum, g) => sum + (all[g.id]?.plays || 0), 0);
}

// ----------------------------------------------------------------------------
// Level / progress
// ----------------------------------------------------------------------------
export interface ProgressRecord {
  level: number;          // last level the player was on
  unlockedLevel: number;  // highest level unlocked
}

type ProgressMap = Partial<Record<GameId, ProgressRecord>>;

export function getProgress(id: GameId): ProgressRecord {
  const all = read<ProgressMap>(K_PROGRESS, {});
  return all[id] || { level: 1, unlockedLevel: 1 };
}

export function setLevel(id: GameId, level: number) {
  const all = read<ProgressMap>(K_PROGRESS, {});
  const prev = all[id] || { level: 1, unlockedLevel: 1 };
  all[id] = { level, unlockedLevel: Math.max(prev.unlockedLevel, level) };
  write(K_PROGRESS, all);
}

/** Unlock the next level (call when a level is cleared). */
export function unlockNext(id: GameId, justCleared: number) {
  const meta = getGame(id);
  const cap = meta ? meta.levels : justCleared + 1;
  const all = read<ProgressMap>(K_PROGRESS, {});
  const prev = all[id] || { level: 1, unlockedLevel: 1 };
  const next = Math.min(cap, justCleared + 1);
  all[id] = { level: prev.level, unlockedLevel: Math.max(prev.unlockedLevel, next) };
  write(K_PROGRESS, all);
}

// ----------------------------------------------------------------------------
// Friends / peers roster (NON-members — the social + retention hook)
// ----------------------------------------------------------------------------
export type FriendKind = 'family' | 'friend' | 'client' | 'peer';

export interface Friend {
  id: string;
  name: string;
  kind: FriendKind;
  /** True once this peer has been invited via a challenge link. */
  invited?: boolean;
  createdAt: string;
}

export const FRIEND_KINDS: { value: FriendKind; label: string; emoji: string }[] = [
  { value: 'family', label: 'Family', emoji: '👨‍👩‍👧' },
  { value: 'friend', label: 'Friend', emoji: '🧑‍🤝‍🧑' },
  { value: 'client', label: 'Client', emoji: '💼' },
  { value: 'peer', label: 'Peer', emoji: '🤝' },
];

export function getFriends(): Friend[] {
  return read<Friend[]>(K_FRIENDS, []);
}

export function addFriend(name: string, kind: FriendKind): Friend {
  const friends = getFriends();
  const f: Friend = {
    id: `f_${Date.now()}_${Math.floor(performance.now() % 1000)}`,
    name: name.trim(),
    kind,
    createdAt: new Date().toISOString(),
  };
  write(K_FRIENDS, [...friends, f]);
  syncFriend(f.id, f.name, f.kind);
  checkAchievements();
  return f;
}

export function removeFriend(id: string) {
  removeFriendRemote(id);
  write(K_FRIENDS, getFriends().filter(f => f.id !== id));
  // also drop their leaderboard rows
  const board = read<BoardMap>(K_BOARD, {});
  for (const g of Object.keys(board) as GameId[]) {
    if (board[g]) delete board[g]![id];
  }
  write(K_BOARD, board);
}

export function markInvited(id: string) {
  const friends = getFriends().map(f => (f.id === id ? { ...f, invited: true } : f));
  write(K_FRIENDS, friends);
  syncFriendInvited(id);
  checkAchievements();
}

// ----------------------------------------------------------------------------
// Social leaderboard (your best + each peer's logged best, per game)
// ----------------------------------------------------------------------------
export interface BoardEntry {
  best: number;
  name: string;
  updatedAt: string;
}

type BoardMap = Partial<Record<GameId, Record<string, BoardEntry>>>;

/** Log a score for any competitor key (self:* or a friend id). Keeps the best. */
export function logScore(id: GameId, competitorKey: string, name: string, score: number) {
  const board = read<BoardMap>(K_BOARD, {});
  const game = board[id] || {};
  const prev = game[competitorKey];
  game[competitorKey] = {
    best: Math.max(prev?.best || 0, score),
    name,
    updatedAt: new Date().toISOString(),
  };
  board[id] = game;
  write(K_BOARD, board);
}

export function logFriendScore(id: GameId, friendId: string, score: number) {
  const friend = getFriends().find(f => f.id === friendId);
  logScore(id, friendId, friend?.name || 'Peer', score);
  // Sync the peer's resulting best (server keeps the max).
  const best = (read<BoardMap>(K_BOARD, {})[id] || {})[friendId]?.best ?? score;
  syncPeerScore(friendId, id, best);
}

export interface LeaderRow {
  key: string;
  name: string;
  best: number;
  isYou: boolean;
  isMember: boolean;
  rank: number;
}

/** Ranked rows for one game: you + every peer who has a logged score. */
export function getLeaderboard(id: GameId): LeaderRow[] {
  const board = read<BoardMap>(K_BOARD, {})[id] || {};
  const me = getPlayer();
  const myKey = `self:${me.id ?? 'me'}`;

  // Make sure you always appear if you have a best.
  const myBest = getHighScore(id);
  if (myBest > 0 && !board[myKey]) {
    board[myKey] = { best: myBest, name: me.name, updatedAt: '' };
  }

  const rows = Object.entries(board)
    .map(([key, e]) => ({
      key,
      name: e.name,
      best: e.best,
      isYou: key === myKey,
      isMember: key.startsWith('self:'),
    }))
    .filter(r => r.best > 0)
    .sort((a, b) => b.best - a.best)
    .map((r, i) => ({ ...r, rank: i + 1 }));

  return rows;
}

// ----------------------------------------------------------------------------
// Challenge links — the viral, attract-non-members loop
// ----------------------------------------------------------------------------
export interface Challenge {
  g: GameId;     // game
  s: number;     // score to beat
  n: string;     // challenger name
  l?: number;    // level
}

/** Encode a challenge to a URL-safe token (base64 of JSON). */
export function encodeChallenge(c: Challenge): string {
  try {
    const json = JSON.stringify(c);
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  } catch {
    return '';
  }
}

export function decodeChallenge(token: string): Challenge | null {
  try {
    const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(b64)));
    const c = JSON.parse(json) as Challenge;
    if (!c || !c.g || typeof c.s !== 'number') return null;
    return c;
  } catch {
    return null;
  }
}

/** Build a full shareable URL. Always targets the PUBLIC /play route so a
 *  non-member can open it without logging in (the GamesPage reads ?challenge=). */
export function buildChallengeUrl(c: Challenge): string {
  const token = encodeChallenge(c);
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/play?challenge=${token}`;
}

/** Read a challenge token from the current URL, if any. */
export function readChallengeFromUrl(): Challenge | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('challenge');
    return token ? decodeChallenge(token) : null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------------
// Daily challenge — a deterministic "game of the day" + a day streak
// ----------------------------------------------------------------------------
const K_DAILY = 'akt_game_daily';

function dateKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** The game everyone gets challenged with today (same for all, changes daily). */
export function getDailyGame(key = dateKey()): GameId {
  return GAMES[hashStr(key) % GAMES.length].id;
}

export interface DailyStatus {
  dateKey: string;
  gameId: GameId;
  doneToday: boolean;
  streak: number;
}

export function getDailyStatus(): DailyStatus {
  const key = dateKey();
  const rec = read<{ lastDoneDate: string; streak: number }>(K_DAILY, { lastDoneDate: '', streak: 0 });
  return {
    dateKey: key,
    gameId: getDailyGame(key),
    doneToday: rec.lastDoneDate === key,
    streak: rec.streak,
  };
}

/** Mark today's daily as completed (called from recordScore when you play it). */
function markDailyDone(id: GameId) {
  const key = dateKey();
  if (id !== getDailyGame(key)) return;
  const rec = read<{ lastDoneDate: string; streak: number }>(K_DAILY, { lastDoneDate: '', streak: 0 });
  if (rec.lastDoneDate === key) return; // already counted today
  const yesterday = dateKey(new Date(Date.now() - 86400000));
  const streak = rec.lastDoneDate === yesterday ? rec.streak + 1 : 1;
  write(K_DAILY, { lastDoneDate: key, streak });
}

// ----------------------------------------------------------------------------
// Achievements / badges
// ----------------------------------------------------------------------------
const K_ACH = 'akt_game_achievements';
const STREAK_GAMES: GameId[] = ['tictactoe', 'connect4', 'pong'];

export interface Achievement {
  id: string;
  title: string;
  desc: string;
  emoji: string;
}

interface AchCtx {
  distinctPlayed: number;
  totalPlays: number;
  friends: number;
  invited: number;
  dailyStreak: number;
  maxUnlocked: number;
  maxBest: number;
  maxStreak: number;
}

const ACH_DEFS: (Achievement & { check: (c: AchCtx) => boolean })[] = [
  { id: 'first_game', title: 'First Coffee Break', desc: 'Play your first game', emoji: '☕', check: c => c.totalPlays >= 1 },
  { id: 'explorer', title: 'Arcade Explorer', desc: 'Play 5 different games', emoji: '🗺️', check: c => c.distinctPlayed >= 5 },
  { id: 'completionist', title: 'Tried Them All', desc: 'Play every game at least once', emoji: '🎯', check: c => c.distinctPlayed >= GAMES.length },
  { id: 'century', title: 'Century', desc: 'Score 100+ in any game', emoji: '💯', check: c => c.maxBest >= 100 },
  { id: 'high_roller', title: 'High Roller', desc: 'Score 1,000+ in any game', emoji: '🏆', check: c => c.maxBest >= 1000 },
  { id: 'persistent', title: 'Just One More', desc: 'Play 25 games', emoji: '🔁', check: c => c.totalPlays >= 25 },
  { id: 'addict', title: 'Productivity? Later.', desc: 'Play 100 games', emoji: '🎰', check: c => c.totalPlays >= 100 },
  { id: 'streak3', title: 'On a Roll', desc: 'Hit a 3-win streak vs the computer', emoji: '🔥', check: c => c.maxStreak >= 3 },
  { id: 'level_up', title: 'Level Climber', desc: 'Unlock level 3 in any game', emoji: '⬆️', check: c => c.maxUnlocked >= 3 },
  { id: 'social', title: 'Bring a Rival', desc: 'Add a friend to your circle', emoji: '🤝', check: c => c.friends >= 1 },
  { id: 'challenger', title: 'Spread the Word', desc: 'Invite a non-member to a challenge', emoji: '📣', check: c => c.invited >= 1 },
  { id: 'daily3', title: 'Habit Forming', desc: '3-day daily-challenge streak', emoji: '📅', check: c => c.dailyStreak >= 3 },
  { id: 'daily7', title: 'Week Warrior', desc: '7-day daily-challenge streak', emoji: '🗓️', check: c => c.dailyStreak >= 7 },
];

export const ALL_ACHIEVEMENTS: Achievement[] = ACH_DEFS.map(({ id, title, desc, emoji }) => ({ id, title, desc, emoji }));

function achContext(): AchCtx {
  const scores = read<ScoreMap>(K_SCORES, {});
  const progress = read<ProgressMap>(K_PROGRESS, {});
  const bests = GAMES.map(g => scores[g.id]?.best || 0);
  const maxStreak = STREAK_GAMES.reduce((m, g) => Math.max(m, scores[g]?.best || 0), 0);
  return {
    distinctPlayed: GAMES.filter(g => (scores[g.id]?.plays || 0) > 0).length,
    totalPlays: GAMES.reduce((s, g) => s + (scores[g.id]?.plays || 0), 0),
    friends: getFriends().length,
    invited: getFriends().filter(f => f.invited).length,
    dailyStreak: getDailyStatus().streak,
    maxUnlocked: GAMES.reduce((m, g) => Math.max(m, progress[g.id]?.unlockedLevel || 1), 1),
    maxBest: Math.max(0, ...bests),
    maxStreak,
  };
}

export function getEarnedAchievementIds(): string[] {
  return read<string[]>(K_ACH, []);
}

/** Recompute earned achievements; persist + return any NEWLY earned (idempotent). */
export function checkAchievements(): Achievement[] {
  const ctx = achContext();
  const already = new Set(getEarnedAchievementIds());
  const earnedNow = ACH_DEFS.filter(a => a.check(ctx)).map(a => a.id);
  const fresh = earnedNow.filter(id => !already.has(id));
  if (fresh.length > 0) {
    write(K_ACH, Array.from(new Set(Array.from(already).concat(earnedNow))));
  }
  return fresh.map(id => ALL_ACHIEVEMENTS.find(a => a.id === id)!).filter(Boolean);
}

export function getAchievementsWithStatus(): (Achievement & { earned: boolean })[] {
  const earned = new Set(getEarnedAchievementIds());
  return ALL_ACHIEVEMENTS.map(a => ({ ...a, earned: earned.has(a.id) }));
}

// ----------------------------------------------------------------------------
// Lifetime stats
// ----------------------------------------------------------------------------
export interface GameStats {
  totalPlays: number;
  distinctPlayed: number;
  favorite: GameMeta | null;
  totalBest: number;
  achievements: { earned: number; total: number };
  dailyStreak: number;
}

export function getStats(): GameStats {
  const scores = read<ScoreMap>(K_SCORES, {});
  let favorite: GameMeta | null = null;
  let favPlays = 0;
  let totalBest = 0;
  for (const g of GAMES) {
    const plays = scores[g.id]?.plays || 0;
    totalBest += scores[g.id]?.best || 0;
    if (plays > favPlays) { favPlays = plays; favorite = g; }
  }
  return {
    totalPlays: totalPlays(),
    distinctPlayed: GAMES.filter(g => (scores[g.id]?.plays || 0) > 0).length,
    favorite,
    totalBest,
    achievements: { earned: getEarnedAchievementIds().length, total: ALL_ACHIEVEMENTS.length },
    dailyStreak: getDailyStatus().streak,
  };
}

// ----------------------------------------------------------------------------
// Full PostgreSQL reconcile — make PG the source of truth so nothing meaningful
// is stranded in localStorage. Pulls the member's server rows, merges with any
// local (offline-created) data taking the best of each, writes the merged result
// back to the local cache, then pushes the union UP so PG holds everything.
// localStorage remains only an offline cache (same pattern as the rest of the app).
// No-ops for guests / when the API isn't reachable.
// ----------------------------------------------------------------------------
export async function reconcileWithServer(): Promise<boolean> {
  const me = getPlayer();
  if (!me.isMember || !hasGamesApi()) return false;

  let serverScores, serverFriends;
  try {
    [serverScores, serverFriends] = await Promise.all([fetchScores(), fetchFriends()]);
  } catch {
    return false;
  }

  const nowIso = new Date().toISOString();

  // --- merge scores (keep the higher best / more plays) ---
  const scoreMap = read<ScoreMap>(K_SCORES, {});
  for (const s of serverScores) {
    const id = s.game_code as GameId;
    const prev = scoreMap[id] || { best: 0, plays: 0, lastScore: 0, updatedAt: '', history: [] };
    scoreMap[id] = {
      best: Math.max(prev.best, s.best_score || 0),
      plays: Math.max(prev.plays, s.plays || 0),
      lastScore: prev.lastScore || s.last_score || 0,
      updatedAt: prev.updatedAt || nowIso,
      history: prev.history,
    };
  }

  // --- merge level progress (game_score.level = highest level reached) ---
  const progMap = read<ProgressMap>(K_PROGRESS, {});
  for (const s of serverScores) {
    const id = s.game_code as GameId;
    const prev = progMap[id] || { level: 1, unlockedLevel: 1 };
    progMap[id] = { level: prev.level, unlockedLevel: Math.max(prev.unlockedLevel, s.level || 1) };
  }

  // --- merge friends + their peer scores ---
  const friends = getFriends();
  const board = read<BoardMap>(K_BOARD, {});
  const serverKeys = new Set(serverFriends.map(f => f.local_key).filter(Boolean) as string[]);
  for (const f of serverFriends) {
    const key = f.local_key || `srv_${f.friend_id}`;
    if (!friends.some(lf => lf.id === key)) {
      friends.push({ id: key, name: f.name, kind: (f.kind as FriendKind) || 'friend', invited: !!f.invited, createdAt: nowIso });
    }
    for (const game of Object.keys(f.scores || {})) {
      const best = (f.scores as Record<string, number>)[game] || 0;
      const g = board[game as GameId] || {};
      g[key] = { best: Math.max(g[key]?.best || 0, best), name: f.name, updatedAt: nowIso };
      board[game as GameId] = g;
    }
  }

  // Persist the merged result locally (this is what the synchronous UI reads).
  write(K_SCORES, scoreMap);
  write(K_PROGRESS, progMap);
  write(K_FRIENDS, friends);
  write(K_BOARD, board);

  // --- push the union UP so PG has everything (upserts keep the max) ---
  try {
    for (const g of GAMES) {
      const sc = scoreMap[g.id];
      if (sc && sc.best > 0) await pushScore(g.id, sc.best, sc.plays, progMap[g.id]?.unlockedLevel || 1);
    }
    for (const f of friends) {
      // Create local-only friends server-side first (ordered before peer scores).
      if (!serverKeys.has(f.id)) {
        const ok = await pushFriend(f.id, f.name, f.kind);
        if (ok && f.invited) syncFriendInvited(f.id);
        if (!ok) continue;
      }
      for (const g of GAMES) {
        const entry = (board[g.id] || {})[f.id];
        if (entry && entry.best > 0) await pushPeerScore(f.id, g.id, entry.best);
      }
    }
  } catch {
    /* best-effort — local already holds the merged truth */
  }

  checkAchievements();
  return true;
}

// ----------------------------------------------------------------------------
// Change subscription helper for React views
// ----------------------------------------------------------------------------
export function onGamesChanged(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener('akt_games_changed', handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('akt_games_changed', handler);
    window.removeEventListener('storage', handler);
  };
}
